import { ReactElement, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import SurveySummaryHeader, { SurveySummaryTab } from "./survey-summary-header.component";
import { useViewSurveyChildren, ViewSurveyChildrenProps } from "./useViewSurveyChildren";
import SurveyMediaPlan from "../view-survey/survey-action-plan/survey-media-plan.component";
import SurveySummaryEntry from "./survey-summary-entry.component";
import { useDispatch } from "react-redux";
import { setSummaryAnimated } from "../../redux/survey/survey-animation.slice";
import SurveySummaryResultsLayout from "./components/survey-results-summary-layout";
import { SurveyMediaPlanStatus } from "src/redux/survey/media-plan.types";

const SurveySummary = ({ surveyId, isShare, isLoadingSummary, isGeneratingSummary, isLoadingMediaPlan, isGeneratingMediaPlan, createSummary, createMediaPlan }: ViewSurveyChildrenProps): ReactElement | null => {
  const { summary, survey, mediaPlan } = useViewSurveyChildren(surveyId, isShare);
  const [activeTab, setActiveTab] = useState<SurveySummaryTab>(SurveySummaryTab.Summary);
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();

  const onTabChange = (tab: SurveySummaryTab) => {
    setActiveTab(tab);
  };

  useEffect(() => {
    const questionIndex = searchParams.get('question');
    if (questionIndex && questionIndex === 'summary') {
      const element = document.getElementById(`survey-summary`);
      element && element.scrollIntoView({ behavior: 'smooth' });
    }
  }, [searchParams]);

  const onSummaryAnimationComplete = () => {
    !isShare && dispatch(setSummaryAnimated({ surveyId }));
  };

  // For now (while all media plans hidden), if accessing via shared link and summary hasn't been generated yet, hide this component entirely
  if (isShare && (!summary || summary.summaries.length === 0)) {
    return null;
  }

  return (
    <div
      className="flex flex-col mb-10 rounded-lg bg-white scroll-m-10"
      id="survey-summary"
    >
      <SurveySummaryHeader
        initialTab={activeTab}
        onTabChange={onTabChange}
        surveyId={surveyId}
        isShare={isShare}
      />
      <div className="p-6">
        {activeTab === SurveySummaryTab.Summary && (
          <SurveySummaryResultsLayout
            survey={survey}
            isEmpty={summary && summary.summaries.length === 0}
            emptyStateText="No summaries exist"
            emptyStateDescription="Click to generate a summary."
            isLoading={!!isLoadingSummary || !!isGeneratingSummary}
            isShare={isShare}
            create={createSummary}
            body={(
              <SurveySummaryEntry
                summaries={summary ? summary.summaries : []}
                surveyId={surveyId}
                onComplete={onSummaryAnimationComplete}
              />
            )}
          />
        )}
        {activeTab === SurveySummaryTab.MediaPlan && (
          <SurveySummaryResultsLayout
            survey={survey}
            isEmpty={mediaPlan && mediaPlan.mediaPlanStatus !== SurveyMediaPlanStatus.Completed}
            emptyStateText="No media plans exist"
            emptyStateDescription="Click to generate a media plan."
            isLoading={!!isLoadingMediaPlan || !!isGeneratingMediaPlan}
            isShare={isShare}
            create={createMediaPlan}
            body={(
              <SurveyMediaPlan
                extSurveyId={surveyId}
                surveyStatus={survey?.surveyStatus}
                useCard={false}
                isShare={isShare}
              />
            )}
          />
        )}
      </div>
    </div>
  );
};

export default SurveySummary;
